import { EChartsOption } from 'echarts';
import dayjs from 'dayjs';
import { isNil, sum } from 'lodash-es';
import { V2EnergyCompareGetMediumIndicatorLossDataPostResponse } from '@maxtropy/device-customer-apis-v2';
import { CycleType } from '@/pages/DeviationAlarmConfig/service';

// 视图数据类型
type ViewVoData = NonNullable<V2EnergyCompareGetMediumIndicatorLossDataPostResponse['viewVoTos']>[number];

// 损耗状态枚举
export enum LossStatus {
  OVER = 1, // 超限
  NORMAL = 2, // 正常
}
export const LossStatusDisplay = {
  [LossStatus.OVER]: '超限',
  [LossStatus.NORMAL]: '正常',
};
// 获取图例
export const getLegend = () => ({
  show: true,
  right: 10,
  top: 10,
  itemWidth: 12,
  itemHeight: 12,
  data: [
    { name: '输入', icon: 'rect' },
    { name: '输出', icon: 'rect' },
    { name: '损耗(超限)', icon: 'rect' },
    { name: '损耗(正常)', icon: 'rect' },
  ],
});
// 斜线填充样式
const biasItemSty = (fullColor: string, biasColor: string) => ({
  color: fullColor,
  decal: {
    color: biasColor,
    dashArrayX: [1, 0],
    dashArrayY: [4, 3],
    rotation: -Math.PI / 4,
  },
});

// 获取偏差率详情图表配置
export const getDeviationDetailChartOption = (
  viewData?: ViewVoData,
  unit?: string,
  statCycleFromTime?: string,
  statCycleToTime?: string,
  statCycleType?: CycleType
): EChartsOption | null => {
  if (!viewData?.processLossViewVoToIns?.length) {
    return null;
  }

  const processLossViewVoToIns = viewData.processLossViewVoToIns;
  const displayUnit = unit || viewData.energyMediumName || '';
  const isHourly = statCycleType === CycleType.hour;

  const fromTime = statCycleFromTime ? dayjs(statCycleFromTime).valueOf() : Date.now();
  const toTime = statCycleToTime ? dayjs(statCycleToTime).valueOf() : Date.now();
  // 计算中间时间
  const midTime = (fromTime + toTime) / 2;

  // 判断时间跨度是否超过24小时
  const isOver24Hours = toTime - fromTime > 24 * 60 * 60 * 1000;

  // 格式化时间标签
  const formatTimeLabel = (time: number) => {
    if (isHourly) {
      // 按小时：超过24小时带日期
      return isOver24Hours ? dayjs(time).format('M-DD HH:mm') : dayjs(time).format('HH:mm');
    } else {
      // 按天：只显示日期
      return dayjs(time).format('M-DD');
    }
  };

  // 计算输入总和
  const entrySum = sum(processLossViewVoToIns.map(i => sum(i.entryDataList?.map(m => m.sum))));
  // 计算输出总和
  const exitSum = sum(processLossViewVoToIns.map(i => sum(i.exitDataList?.map(m => m.sum))));
  // 计算损耗
  const loss = sum(processLossViewVoToIns.map(i => i.lossDataVo?.loss ?? 0));
  // 是否超限
  const hasExceed = processLossViewVoToIns.some(i => i.lossDataVo?.ifExceed);

  return {
    grid: {
      left: 60,
      right: 60,
      top: 55,
      bottom: 50,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(0,0,0,0.8)',
      borderColor: 'transparent',
      textStyle: { color: '#fff' },
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#999',
          type: 'dashed',
        },
      },
      formatter() {
        const timeRange = `${formatTimeLabel(fromTime)} ~ ${formatTimeLabel(toTime)}`;
        const rowStyle = 'display:flex; justify-content:space-between; align-items: flex-end; width:250px;';
        const leftTextStyle = 'flex:1;word-break:break-word; white-space:normal;padding-right: 16px;';
        // 图例颜色的小圆点(使用border-radius:50%实现圆形)
        const entryMarker = '<span style="display:inline-block;width:10px;height:10px;background:#2D8DFF;margin-right:5px;border-radius:50%;"></span>';
        const exitMarker = '<span style="display:inline-block;width:10px;height:10px;background:#16DD8E;margin-right:5px;border-radius:50%;"></span>';
        const lossOverMarker = '<span style="display:inline-block;width:10px;height:10px;background:rgba(179, 179, 179, 1);margin-right:5px;border-radius:50%;"></span>';
        const lossNormalMarker = '<span style="display:inline-block;width:10px;height:10px;background:rgb(255, 203, 71);margin-right:5px;border-radius:50%;"></span>';
        let str = timeRange;

        // 收集所有入口和出口的详细数据
        const allEntryList = processLossViewVoToIns.flatMap(i => i.entryDataList || []);
        const allExitList = processLossViewVoToIns.flatMap(i => i.exitDataList || []);

        // 输入(显示汇总+详细列表)
        if (!isNil(entrySum)) {
          str += `
            <div style="${rowStyle}">
              <span style="${leftTextStyle}">${entryMarker}输入--${viewData.energyMediumName || ''}</span>
              <span style="white-space:nowrap;">${!isNaN(entrySum) ? entrySum.toFixed(2) : '--'}${displayUnit}</span>
            </div>`;
          // 详细列表
          allEntryList.forEach((i: any) => {
            const value = !isNil(i.sum) ? `${i.sum.toFixed(2)}${displayUnit}` : '--';
            str += `
              <div style="${rowStyle}">
                <span style="${leftTextStyle}padding-left:16px;">${i.entryExitName || '--'}</span>
                <span style="white-space:nowrap;">${value}</span>
              </div>`;
          });
        }

        // 输出(显示汇总+详细列表)
        if (!isNil(exitSum)) {
          str += `
            <div style="${rowStyle}">
              <span style="${leftTextStyle}">${exitMarker}输出--${viewData.energyMediumName || ''}</span>
              <span style="white-space:nowrap;">${!isNaN(exitSum) ? exitSum.toFixed(2) : '--'}${displayUnit}</span>
            </div>`;
          // 详细列表
          allExitList.forEach((i: any) => {
            const value = !isNil(i.sum) ? `${i.sum.toFixed(2)}${displayUnit}` : '--';
            str += `
              <div style="${rowStyle}">
                <span style="${leftTextStyle}padding-left:16px;">${i.entryExitName || '--'}</span>
                <span style="white-space:nowrap;">${value}</span>
              </div>`;
          });
        }

        // 损耗（只显示超限或正常其中一个）
        if (!isNil(loss)) {
          const lossName = hasExceed ? '损耗(超限)' : '损耗(正常)';
          const lossMarker = hasExceed ? lossOverMarker : lossNormalMarker;
          str += `
            <div style="${rowStyle}">
              <span style="${leftTextStyle}">${lossMarker}${lossName}</span>
              <span style="white-space:nowrap;">${Math.abs(loss).toFixed(2)}${displayUnit}</span>
            </div>`;
        }

        return str;
      },
    },
    legend: getLegend(),
    xAxis: {
      type: 'time',
      min: fromTime,
      max: toTime,
      axisLine: { show: true },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        fontSize: 12,
        interval: 0,
        showMinLabel: true,
        showMaxLabel: true,
        formatter: (value: number) => {
          if (Math.abs(value - fromTime) < 60000) {
            return formatTimeLabel(fromTime);
          }
          if (Math.abs(value - toTime) < 60000) {
            return formatTimeLabel(toTime);
          }
          return '';
        },
      },
    },
    yAxis: {
      type: 'value',
      name: displayUnit,
      position: 'left',
      nameTextStyle: { fontSize: 14, align: 'right' },
      nameGap: 30,
      splitLine: {
        lineStyle: { color: '#FFFFFF30' },
      },
    },
    series: [
      // 输入
      {
        name: '输入',
        type: 'bar',
        color: '#2D8DFF',
        barWidth: 60,
        emphasis: { focus: 'series' },
        data: [[midTime, !isNaN(entrySum) ? entrySum : null]],
      },
      // 输出 - 与损耗堆叠
      {
        name: '输出',
        type: 'bar',
        stack: 'output-loss',
        color: '#16DD8E',
        barWidth: 60,
        emphasis: { focus: 'series' },
        data: [[midTime, !isNaN(exitSum) ? exitSum : null]],
      },
      // 损耗(超限)
      {
        name: '损耗(超限)',
        type: 'bar',
        stack: 'output-loss',
        barWidth: 60,
        emphasis: { focus: 'series' },
        itemStyle: biasItemSty('rgba(179, 179, 179, 0.4)', 'rgba(179, 179, 179, 1)'),
        data: [[midTime, hasExceed && !isNil(loss) ? Math.abs(loss) : null]],
      },
      // 损耗(正常)
      {
        name: '损耗(正常)',
        type: 'bar',
        stack: 'output-loss',
        barWidth: 60,
        emphasis: { focus: 'series' },
        itemStyle: biasItemSty('rgba(255, 203, 71, 0.4)', 'rgb(255, 203, 71)'),
        data: [[midTime, !hasExceed && !isNil(loss) ? Math.abs(loss) : null]],
      },
    ],
  };
}
